import { useState, useEffect, useRef } from 'react'
import Hls from 'hls.js'
import mpegts from 'mpegts.js'
import { fetchPlaylist, getGroups, searchChannels } from '../services/iptv'
import { isElectron, getElectronProxyUrl } from '../utils/electron'
import Loader from '../components/common/Loader'
import './LiveTV.css'

// CORS Proxy for streams (web uses Cloudflare, Electron uses local proxy)
const CORS_PROXY_URL = import.meta.env.VITE_CORS_PROXY_URL

function LiveTV() {
  const [channels, setChannels] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedGroup, setSelectedGroup] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeChannel, setActiveChannel] = useState(null)
  const [playerError, setPlayerError] = useState(null)
  const [playerLoading, setPlayerLoading] = useState(false)
  const [displayCount, setDisplayCount] = useState(50) // Only show 50 channels initially
  const videoRef = useRef(null)
  const hlsRef = useRef(null)
  const mpegtsRef = useRef(null)

  // Load playlist on mount
  useEffect(() => {
    loadPlaylist()
  }, [])

  // Filter channels when search or group changes - reset displayCount on filter change
  useEffect(() => {
    if (!loading) {
      const filtered = searchChannels(searchQuery, selectedGroup)
      setChannels(filtered)
      setDisplayCount(50) // Reset pagination when filters change
    }
  }, [searchQuery, selectedGroup, loading])

  // Load more channels
  const loadMore = () => {
    setDisplayCount(prev => prev + 50)
  }

  // Get visible channels (pagination)
  const visibleChannels = channels.slice(0, displayCount)

  const loadPlaylist = async () => {
    setLoading(true)
    await fetchPlaylist()
    const allGroups = getGroups()
    setGroups(allGroups)
    
    // Set initial channels (all)
    const allChannels = searchChannels('', 'all')
    setChannels(allChannels)
    setLoading(false)
  }

  const getProxiedUrl = (url) => {
    // In Electron, use local proxy server
    if (isElectron()) {
      const proxyUrl = getElectronProxyUrl()
      if (proxyUrl) {
        return `${proxyUrl}?url=${encodeURIComponent(url)}`
      }
    }
    // On web, use Cloudflare proxy
    if (!CORS_PROXY_URL) return url
    return `${CORS_PROXY_URL}?url=${encodeURIComponent(url)}`
  }

  // Detect stream type from URL
  const getStreamType = (url) => {
    const lowerUrl = url.toLowerCase()
    // Remove URL hash for extension detection, but keep query params
    // since some IPTV URLs have extension before # (like ...file%23.mp4)
    const urlForExt = lowerUrl.split('#')[0]
    // Get the path without query params for extension check
    const pathOnly = urlForExt.split('?')[0]
    
    // HLS streams
    if (lowerUrl.includes('.m3u8') || lowerUrl.includes('m3u8')) return 'hls'
    // Native video formats (MP4, WebM, etc.) - use direct playback
    // Only match if the extension is at the END of the path
    if (pathOnly.endsWith('.mp4') || pathOnly.endsWith('.webm') || 
        pathOnly.endsWith('.mkv') || pathOnly.endsWith('.avi') ||
        pathOnly.endsWith('.mov')) return 'native'
    // Explicit TS files (not in /live/ path which are streams)
    if (pathOnly.endsWith('.ts') && !lowerUrl.includes('/live/')) return 'ts'
    // FLV streams
    if (lowerUrl.includes('.flv')) return 'flv'
    // Default: treat as mpegts for live IPTV streams
    return 'ts'
  }

  const playChannel = async (channel) => {
    setActiveChannel(channel)
    setPlayerError(null)
    setPlayerLoading(true)
    
    // Wait for video element to mount
    setTimeout(async () => {
      if (!videoRef.current) return
      
      const video = videoRef.current
      
      // Clean up previous instances
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
      if (mpegtsRef.current) {
        mpegtsRef.current.destroy()
        mpegtsRef.current = null
      }
      
      // Proxy the stream URL
      const streamUrl = getProxiedUrl(channel.url)
      const streamType = getStreamType(channel.url)
      
      console.log(`Playing ${channel.name}: ${streamType} stream`, streamUrl)
      
      if (streamType === 'hls' && Hls.isSupported()) {
        // Use HLS.js
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          xhrSetup: function(xhr, url) {
            xhr.withCredentials = false
          }
        })
        hlsRef.current = hls
        
        hls.loadSource(streamUrl)
        hls.attachMedia(video)
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setPlayerLoading(false)
          video.play().catch(e => console.log('Autoplay blocked:', e))
        })
        
        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS Error:', data)
          if (data.fatal) {
            setPlayerLoading(false)
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                setPlayerError('Error de red - Canal no disponible')
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError()
                break
              default:
                setPlayerError('Error al cargar el canal')
                break
            }
          }
        })
      } else if ((streamType === 'ts' || streamType === 'flv') && mpegts.isSupported()) {
        // Use mpegts.js for MPEG-TS and FLV streams
        const player = mpegts.createPlayer({
          type: streamType === 'flv' ? 'flv' : 'mpegts',
          url: streamUrl,
          isLive: true
        }, {
          enableWorker: true,
          enableStashBuffer: true,
          stashInitialSize: 1024 * 384, // 384KB initial buffer
          lazyLoad: false,
          lazyLoadMaxDuration: 3 * 60,
          lazyLoadRecoverDuration: 30,
          deferLoadAfterSourceOpen: false,
          autoCleanupSourceBuffer: true,
          autoCleanupMaxBackwardDuration: 3 * 60,
          autoCleanupMinBackwardDuration: 2 * 60
        })
        mpegtsRef.current = player
        
        player.attachMediaElement(video)
        player.load()
        
        player.on(mpegts.Events.MEDIA_INFO, () => {
          setPlayerLoading(false)
          video.play().catch(e => console.log('Autoplay blocked:', e))
        })
        
        player.on(mpegts.Events.ERROR, (errorType, errorDetail) => {
          console.error('MPEGTS Error:', errorType, errorDetail)
          // Destroy player to stop error loop
          if (mpegtsRef.current) {
            mpegtsRef.current.destroy()
            mpegtsRef.current = null
          }
          setPlayerLoading(false)
          setPlayerError('Error al cargar el canal')
        })
      } else if (streamType === 'native') {
        // Native video playback for MP4, WebM, etc.
        video.src = streamUrl
        video.onloadeddata = () => {
          setPlayerLoading(false)
          video.play().catch(e => console.log('Autoplay blocked:', e))
        }
        video.onerror = () => {
          const err = video.error
          console.error('Native video error:', err?.code, err?.message)
          setPlayerLoading(false)
          // Code 4 = SRC_NOT_SUPPORTED (usually codec issue like HEVC)
          if (err?.code === 4 || err?.message?.includes('DEMUXER')) {
            setPlayerError('Codec no soportado - Usa VLC')
          } else if (err?.code === 2) {
            setPlayerError('Error de red')
          } else {
            setPlayerError('Error al cargar el video')
          }
        }
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari/iOS)
        video.src = streamUrl
        video.addEventListener('loadedmetadata', () => {
          setPlayerLoading(false)
          video.play().catch(e => console.log('Autoplay blocked:', e))
        })
        video.addEventListener('error', () => {
          setPlayerLoading(false)
          setPlayerError('Error al cargar el canal')
        })
      } else {
        // Fallback - try direct playback
        video.src = streamUrl
        video.onloadeddata = () => setPlayerLoading(false)
        video.onerror = () => {
          setPlayerLoading(false)
          setPlayerError('Formato no soportado')
        }
        video.play().catch(e => {
          console.log('Cannot play:', e)
          setPlayerLoading(false)
          setPlayerError('Formato no soportado')
        })
      }
    }, 100)
  }

  const closePlayer = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }
    if (mpegtsRef.current) {
      mpegtsRef.current.destroy()
      mpegtsRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.src = ''
    }
    setActiveChannel(null)
    setPlayerError(null)
    setPlayerLoading(false)
  }

  // Open in VLC
  const openInVLC = (url) => {
    if (window.electronAPI?.openInVLC) {
      window.electronAPI.openInVLC(url)
    } else {
      // Fallback for web
      window.open(`vlc://${url}`, '_blank')
    }
  }

  return (
    <div className="livetv-page container">
      {/* Header */}
      <div className="livetv-header">
        <h1>📺 TV en vivo</h1>
        <span className="channel-count">{channels.length} canales</span>
      </div>

      {/* Filters */}
      <div className="livetv-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Buscar canal..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
          className="group-select"
        >
          <option value="all">Todos los países</option>
          {groups.map(group => (
            <option key={group} value={group}>{group}</option>
          ))}
        </select>
      </div>

      {/* Channel Grid */}
      {loading ? (
        <Loader />
      ) : channels.length === 0 ? (
        <div className="no-channels">
          <p>No se encontraron canales</p>
        </div>
      ) : (
        <>
          <div className="channels-grid">
            {visibleChannels.map(channel => (
              <div
                key={channel.id}
                className="channel-card"
                onClick={() => playChannel(channel)}
              >
                <div className="channel-logo">
                  <img 
                    src={channel.logo || '/placeholder-tv.svg'} 
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/placeholder-tv.svg'
                      e.target.onerror = null
                    }}
                  />
                </div>
                <div className="channel-info">
                  <span className="channel-name">{channel.name}</span>
                  <span className="channel-group">{channel.group}</span>
                </div>
              </div>
            ))}
          </div>
          {displayCount < channels.length && (
            <div className="load-more-container">
              <button className="btn btn-primary load-more-btn" onClick={loadMore}>
                Cargar más ({channels.length - displayCount} restantes)
              </button>
            </div>
          )}
        </>
      )}

      {/* Player Modal */}
      {activeChannel && (
        <div className="player-modal" onClick={closePlayer}>
          <div className="player-container" onClick={(e) => e.stopPropagation()}>
            <div className="player-header">
              <h3>{activeChannel.name}</h3>
              <div className="player-actions">
                <button 
                  className="vlc-btn" 
                  onClick={() => openInVLC(activeChannel.url)}
                  title="Abrir en VLC"
                >
                  📱 VLC
                </button>
                <button className="close-btn" onClick={closePlayer}>✕</button>
              </div>
            </div>
            <div className="player-wrapper">
              {playerLoading && (
                <div className="player-loading">
                  <Loader />
                  <span>Cargando canal...</span>
                </div>
              )}
              {playerError ? (
                <div className="player-error">
                  <span>❌ {playerError}</span>
                  <div className="error-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={() => openInVLC(activeChannel.url)}
                    >
                      📱 Abrir en VLC
                    </button>
                    <a 
                      href={activeChannel.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="open-external"
                    >
                      🔗 URL directa
                    </a>
                  </div>
                </div>
              ) : (
                <video
                  ref={videoRef}
                  controls
                  autoPlay
                  playsInline
                  className="video-player"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LiveTV
